// Cartesia speech, straight from the browser with an access token. One websocket, opened
// when first needed and reused; each message gets its own context, and the raw float PCM
// that streams back is scheduled gaplessly on the shared AudioContext.

const SOCKET_URL = 'wss://api.cartesia.ai/tts/websocket';
const SAMPLE_RATE = 44100;
// A socket whose token is this close to expiry is replaced, between messages.
const REFRESH_MARGIN_MS = 60_000;
// Playback starts slightly ahead of "now" so the first chunk is not clipped.
const START_LEAD_SECONDS = 0.05;

export type CartesiaAccess = {
  token: string;
  expiresInSeconds: number;
  voiceId: string;
  modelId: string;
  version: string;
  speed: number;
  emotion: string;
};

export type SpeakResult = 'done' | 'cancelled' | 'failed';

export type Speaker = {
  speak: (text: string) => Promise<SpeakResult>;
  /** Stops what is playing, tells the vendor to stop, and resolves the pending `speak`. */
  stopAll: () => void;
  /** Closes the socket for good. */
  close: () => void;
};

export type SpeakerOptions = {
  /** The access the first socket uses (it came with the voice tokens). */
  initial: CartesiaAccess;
  /** Fresh access, asked for only when the current token is about to expire. */
  getToken: () => Promise<CartesiaAccess>;
  audioContext: AudioContext;
  /**
   * The vendor connection closed while a message was being spoken, or could not be opened
   * for one. Voice mode ends.
   */
  onDropped: () => void;
  /** Characters submitted to the vendor for one message, and the model they were sent to. */
  onUsage: (characters: number, modelId: string) => void;
};

type Pending = {
  contextId: string;
  nextStart: number;
  sources: AudioBufferSourceNode[];
  resolve: (result: SpeakResult) => void;
  finished: boolean;
  ended: number;
};

type Live = {
  socket: WebSocket;
  opened: Promise<void>;
  expiresAt: number;
  access: CartesiaAccess;
};

function decodeChunk(base64: string): Float32Array<ArrayBuffer> {
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Float32Array(bytes.buffer, 0, Math.floor(bytes.length / 4));
}

export function createSpeaker(options: SpeakerOptions): Speaker {
  const { audioContext } = options;
  let live: Live | null = null;
  let pending: Pending | null = null;
  let access = options.initial;
  let accessExpiresAt = Date.now() + access.expiresInSeconds * 1000;
  let closed = false;
  let counter = 0;
  // Bumped by every stop, so a `speak` still waiting on the socket can tell it was cancelled.
  let epoch = 0;
  // Speaks still waiting on the socket; a stop releases them without waiting for it.
  const waiting = new Set<() => void>();
  const releaseWaiting = (): void => {
    waiting.forEach((release) => release());
    waiting.clear();
  };

  const open = (): Live => {
    const query = new URLSearchParams({
      access_token: access.token,
      cartesia_version: access.version,
    });
    const socket = new WebSocket(`${SOCKET_URL}?${query.toString()}`);
    const opened = new Promise<void>((resolve, reject) => {
      socket.onopen = () => resolve();
      socket.onerror = () => reject(new Error('socket error'));
    });
    // The rejection is handled where `speak` awaits it; this keeps an unused one quiet.
    opened.catch(() => undefined);
    const mine: Live = { socket, opened, expiresAt: accessExpiresAt, access };
    socket.onmessage = (event: MessageEvent<unknown>) => {
      if (typeof event.data === 'string') {
        onMessage(event.data);
      }
    };
    socket.onclose = () => {
      if (live !== mine) {
        // Closed by this speaker on purpose (a planned reopen, or `close`).
        return;
      }
      live = null;
      // An idle socket the vendor timed out costs nothing: the next message reopens it.
      // Only a close while a message is being spoken is a dropped connection.
      if (pending !== null && !pending.finished) {
        const lost = pending;
        pending = null;
        lost.sources.forEach((source) => safeStop(source));
        lost.resolve('failed');
        if (!closed) {
          options.onDropped();
        }
      }
    };
    return mine;
  };

  const safeStop = (source: AudioBufferSourceNode): void => {
    source.onended = null;
    try {
      source.stop();
    } catch {
      // Never started, or already stopped.
    }
  };

  const onMessage = (raw: string): void => {
    let message: Record<string, unknown>;
    try {
      message = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }
    const current = pending;
    if (current === null || message['context_id'] !== current.contextId) {
      // For a context that was cancelled or finished: nothing to do.
      return;
    }
    if (message['type'] === 'chunk' && typeof message['data'] === 'string') {
      schedule(current, decodeChunk(message['data']));
    } else if (message['type'] === 'done') {
      finish(current);
    } else if (message['type'] === 'error') {
      current.sources.forEach((source) => safeStop(source));
      pending = null;
      current.resolve('failed');
    }
  };

  const schedule = (current: Pending, samples: Float32Array<ArrayBuffer>): void => {
    if (samples.length === 0) {
      return;
    }
    const buffer = audioContext.createBuffer(1, samples.length, SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    const startAt = Math.max(
      current.nextStart,
      audioContext.currentTime + START_LEAD_SECONDS,
    );
    source.onended = () => {
      current.ended += 1;
      complete(current);
    };
    source.start(startAt);
    current.nextStart = startAt + buffer.duration;
    current.sources.push(source);
  };

  // The message is done when the vendor has said so and every scheduled chunk has played,
  // in whichever order those two happen.
  const complete = (current: Pending): void => {
    if (!current.finished || current.ended < current.sources.length) {
      return;
    }
    if (pending === current) {
      pending = null;
    }
    current.resolve('done');
  };

  const finish = (current: Pending): void => {
    current.finished = true;
    complete(current);
  };

  const ensureLive = async (): Promise<Live> => {
    if (live !== null && live.expiresAt - Date.now() < REFRESH_MARGIN_MS) {
      const old = live;
      live = null;
      old.socket.close();
    }
    if (live === null) {
      if (accessExpiresAt - Date.now() < REFRESH_MARGIN_MS) {
        access = await options.getToken();
        accessExpiresAt = Date.now() + access.expiresInSeconds * 1000;
      }
      live = open();
    }
    const current = live;
    await current.opened;
    return current;
  };

  return {
    async speak(text) {
      if (closed) {
        return 'failed';
      }
      if (audioContext.state === 'suspended') {
        void audioContext.resume();
      }
      counter += 1;
      const mine = epoch;
      const contextId = `${Date.now().toString(36)}-${counter}`;
      let connection: Live | null;
      try {
        let release = (): void => undefined;
        const released = new Promise<null>((resolve) => {
          release = () => resolve(null);
          waiting.add(release);
        });
        const opening = ensureLive();
        opening.catch(() => undefined);
        connection = await Promise.race([opening, released]);
        waiting.delete(release);
      } catch {
        // Could not connect: the same as a dropped connection. (A socket that errors while
        // opening also closes, but with nothing pending, so its close reports nothing.)
        if (!closed) {
          options.onDropped();
        }
        return 'failed';
      }
      if (mine !== epoch || connection === null) {
        return 'cancelled';
      }
      if (closed) {
        return 'failed';
      }
      if (mine !== epoch) {
        return 'cancelled';
      }
      return new Promise<SpeakResult>((resolve) => {
        pending = {
          contextId,
          nextStart: 0,
          sources: [],
          resolve,
          finished: false,
          ended: 0,
        };
        connection.socket.send(
          JSON.stringify({
            model_id: connection.access.modelId,
            transcript: text,
            voice: connection.access.voiceId,
            output_format: {
              container: 'raw',
              encoding: 'pcm_f32le',
              sample_rate: SAMPLE_RATE,
            },
            generation_config: {
              speed: connection.access.speed,
              emotion: connection.access.emotion,
            },
            language: 'en',
            context_id: contextId,
            continue: false,
          }),
        );
        options.onUsage(text.length, connection.access.modelId);
      });
    },
    stopAll() {
      epoch += 1;
      releaseWaiting();
      const current = pending;
      pending = null;
      if (current === null) {
        return;
      }
      current.sources.forEach((source) => safeStop(source));
      if (!current.finished && live?.socket.readyState === WebSocket.OPEN) {
        live.socket.send(JSON.stringify({ context_id: current.contextId, cancel: true }));
      }
      current.resolve('cancelled');
    },
    close() {
      closed = true;
      epoch += 1;
      releaseWaiting();
      const current = pending;
      pending = null;
      current?.sources.forEach((source) => safeStop(source));
      current?.resolve('cancelled');
      const old = live;
      live = null;
      old?.socket.close();
    },
  };
}
